import type { WindowTweet } from '../api/af-social-tracker-api';

export type SyncResultRecord = {
  state?: string;
  note?: string;
  monitoring_since?: string | Date;
  monitoring_until?: string | Date;
  monitoringSince?: string | Date;
  monitoringUntil?: string | Date;
  [key: string]: unknown;
};

export type TweetStatsSnapshot = {
  views: number;
  likes: number;
  retweets: number;
  replies: number;
  quotes: number;
  bookmarks: number;
};

export const toEpochMs = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = new Date(String(value)).getTime();
  return Number.isFinite(t) ? t : null;
};

export const parseSyncResultBounds = (
  r: SyncResultRecord,
): { sinceMs: number; untilMs: number } | null => {
  const raw = r as Record<string, unknown>;
  const sinceVal =
    raw.monitoring_since ??
    raw.monitoringSince ??
    raw.from ??
    raw.since_time ??
    raw.sinceTime;
  const untilVal =
    raw.monitoring_until ??
    raw.monitoringUntil ??
    raw.to ??
    raw.until_time ??
    raw.untilTime;

  const sinceMs = toEpochMs(sinceVal);
  const untilMs = toEpochMs(untilVal);
  if (sinceMs == null || untilMs == null) return null;
  if (untilMs < sinceMs) return null;
  return { sinceMs, untilMs };
};

export const tweetCreatedMs = (t: WindowTweet): number => {
  for (const c of [t.createdAtDate, t.createdAt]) {
    if (c == null || c === '') continue;
    const ms = new Date(c as string).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return NaN;
};

export type RowTweetBuckets = {
  highFollowerTweets: WindowTweet[];
  adminTweets: WindowTweet[];
  modTweets: WindowTweet[];
  replyTweets: WindowTweet[];
  quoteTweets: WindowTweet[];
  /** X_ACCOUNT: timeline posts by the monitored handle. */
  feedTweets: WindowTweet[];
  isLoading: boolean;
  loaded: boolean;
};

export const EMPTY_ROW_TWEET_BUCKETS: RowTweetBuckets = {
  highFollowerTweets: [],
  adminTweets: [],
  modTweets: [],
  replyTweets: [],
  quoteTweets: [],
  feedTweets: [],
  isLoading: false,
  loaded: false,
};

export const createRowTweetBuckets = (loaded: boolean): RowTweetBuckets => ({
  highFollowerTweets: [],
  adminTweets: [],
  modTweets: [],
  replyTweets: [],
  quoteTweets: [],
  feedTweets: [],
  isLoading: false,
  loaded,
});

const num = (v: unknown): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

export const parseTweetStatsFromResult = (r: SyncResultRecord): TweetStatsSnapshot | null => {
  const raw = r as Record<string, unknown>;
  const tweet = (raw.tweet ?? raw.Tweet) as Record<string, unknown> | undefined;
  const nested = tweet?.tweet_stats ?? tweet?.tweetStats;
  const flat = raw.tweet_stats ?? raw.tweetStats;
  const s = (nested ?? flat) as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return null;
  return {
    views: num(s.views),
    likes: num(s.likes),
    retweets: num(s.retweets),
    replies: num(s.replies),
    quotes: num(s.quotes),
    bookmarks: num(s.bookmarks),
  };
};

export const formatStatDelta = (
  current: number,
  previous: number | null,
): { absText: string; pctText: string | null; positive: boolean | null } => {
  if (previous === null) {
    return { absText: '—', pctText: null, positive: null };
  }
  const delta = current - previous;
  const sign = delta > 0 ? '+' : '';
  const absText = `${sign}${delta.toLocaleString()}`;
  if (previous === 0) {
    return {
      absText,
      pctText: current === 0 ? '(0%)' : '(new)',
      positive: delta > 0 ? true : delta < 0 ? false : null,
    };
  }
  const pct = (delta / previous) * 100;
  const pctSign = pct > 0 ? '+' : '';
  return {
    absText,
    pctText: `(${pctSign}${pct.toFixed(1)}%)`,
    positive: delta > 0 ? true : delta < 0 ? false : null,
  };
};
